import { showError, showSuccess } from 'src/toasts/toasts';
import { GroupMember } from '../grouping';
import { ExpenditureRow, IncomeRow, LifeEventRow } from '../rows';
import { JointExpenditureFormModal } from './JointExpenditureFormModal';
import { JointIncomeFormModal } from './JointIncomeFormModal';
import { JointLifeEventFormModal } from './JointLifeEventFormModal';
import { ownersRefusal } from './jointAllocations';
import {
  useDeleteJointExpenditure,
  useDeleteJointIncome,
  useDeleteJointLifeEvent,
  useSaveJointExpenditure,
  useSaveJointIncome,
  useSaveJointLifeEvent,
} from './useJointSaves';

type OpenedModal<TRow> = {
  opened: boolean;
  row?: TRow;
  /** Closes the form and forgets the row it was opened on. */
  onClose: () => void;
};

/**
 * The forms of a tab whose cashflow items are held by owners: each item is saved and deleted
 * whole, in one request, so it either lands or does not and there is no partial save to report.
 */
export function JointCashflowModals({
  members,
  defaultClientId,
  viewingClientId,
  income,
  expenditure,
  lifeEvent,
}: {
  members: GroupMember[];
  /** The member a new item starts with, and the client requests go through where it can. */
  defaultClientId?: string;
  /** The client whose own tab the forms open from; absent on the family tab. */
  viewingClientId?: string;
  income: OpenedModal<IncomeRow>;
  expenditure: OpenedModal<ExpenditureRow>;
  lifeEvent: OpenedModal<LifeEventRow>;
}) {
  const preferred = [
    ...(defaultClientId ? [defaultClientId] : []),
    ...members.map(member => member.clientId),
  ];
  const saveIncome = useSaveJointIncome(preferred);
  const saveExpenditure = useSaveJointExpenditure(preferred);
  const saveLifeEvent = useSaveJointLifeEvent(preferred);
  const deleteIncome = useDeleteJointIncome(preferred);
  const deleteExpenditure = useDeleteJointExpenditure(preferred);
  const deleteLifeEvent = useDeleteJointLifeEvent(preferred);

  const save =
    <T,>(persist: (submission: T) => Promise<void>, noun: string, close: () => void) =>
    async (submission: T) => {
      try {
        await persist(submission);
        showSuccess(`${noun} saved successfully`);
        close();
      } catch (error) {
        // A refusal of the owners is the form's to show beside them, and it stays open to fix them
        if (ownersRefusal(error)) throw error;
        showError(`Failed to save ${noun.toLowerCase()}`);
      }
    };

  // A failed delete is left to the warning dialog, which stays open and says so.
  const remove =
    <T,>(discard: (deletion: T) => Promise<void>, noun: string, close: () => void) =>
    async (deletion: T) => {
      await discard(deletion);
      showSuccess(`${noun} deleted`);
      close();
    };

  return (
    <>
      <JointIncomeFormModal
        opened={income.opened}
        onClose={income.onClose}
        onSubmit={save(saveIncome, 'Income', income.onClose)}
        onDelete={remove(deleteIncome, 'Income', income.onClose)}
        members={members}
        defaultClientId={defaultClientId}
        viewingClientId={viewingClientId}
        initialRow={income.row}
      />
      <JointExpenditureFormModal
        opened={expenditure.opened}
        onClose={expenditure.onClose}
        onSubmit={save(saveExpenditure, 'Expenditure', expenditure.onClose)}
        onDelete={remove(deleteExpenditure, 'Expenditure', expenditure.onClose)}
        members={members}
        defaultClientId={defaultClientId}
        viewingClientId={viewingClientId}
        initialRow={expenditure.row}
      />
      <JointLifeEventFormModal
        opened={lifeEvent.opened}
        onClose={lifeEvent.onClose}
        onSubmit={save(saveLifeEvent, 'Life event', lifeEvent.onClose)}
        onDelete={remove(deleteLifeEvent, 'Life event', lifeEvent.onClose)}
        members={members}
        defaultClientId={defaultClientId}
        viewingClientId={viewingClientId}
        initialRow={lifeEvent.row}
      />
    </>
  );
}
